import axios from 'axios';
import { IPublicClientApplication } from "@azure/msal-browser";
import { apiRequest } from '../authConfig';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

export interface ApiUser {
    id: number;
    azureAdObjectId: string;
    displayName: string;
    email: string;
    userRole: string;
}

export const loginUser = async (instance: IPublicClientApplication, account: any): Promise<ApiUser> => {
    const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: account
    });
    const loginData = {
        azureAdObjectId: account.localAccountId,
        displayName: account.name,
        email: account.username
    }
    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json'
        }
    };

    try {
        const apiUrl = `${API_URL}/users/user/login`;
        const apiResponse = await axios.post<ApiUser>(apiUrl, loginData, axiosConfig);
        return apiResponse.data;
    } catch (error) {
        console.error("Error during user login/registration", error);
        throw error;
    }
}

export const getTickets = async (instance: IPublicClientApplication, account: any, status: string): Promise<TicketDetail[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const apiResponse = await axios.get<TicketDetail[]>(`${API_URL}/tickets/${status}`, config);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error fetching ${status} tickets:`, error);
        throw error;
    }
};

export const getTicket = async (instance: IPublicClientApplication, account: any, ticketId: string): Promise<TicketDetail> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const apiUrl = `${API_URL}/tickets/${ticketId}`;
        const apiResponse = await axios.get<TicketDetail>(apiUrl, config);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error fetching details for ticket ${ticketId}:`, error);
        throw error;
    }
};

export const updateTicket = async (
    instance: IPublicClientApplication,
    account: any,
    ticketId: string,
    updatedData: { [key: string]: string }
): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const apiUrl = `${API_URL}/tickets/${ticketId}`;
        await axios.put(apiUrl, updatedData, config);
    } catch (error) {
        console.error(`Error updating details for ticket ${ticketId}:`, error);
        throw error;
    }
};

export const deleteTickets = async (
    instance: IPublicClientApplication,
    account: any,
    ticketIds: string[],
    reason: string
): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { 
        headers: { Authorization: `Bearer ${response.accessToken}` },
        data: { reason } 
    };
    try {
        await Promise.all(ticketIds.map(id => axios.delete(`${API_URL}/tickets/${id}`, config)));
    } catch (error) {
        console.error(`Error deleting tickets:`, error);
        throw error;
    }
};

export interface TicketDetail {
    id: number;
    blobPath: string;
    status: string;
    receivedTimestamp: string;
    errorMessage: string | null;
    branch: string | null;
    orderType: string | null;
    orderDate: string | null;
    postPeriod: string | null;
    vendorID: string | null;
    siteid: string | null;
    warehouse: string | null;
    inventoryID: string | null;
    description: string | null;
    uom: string | null;
    receivedQty: number | null;
    freightVendorID: string | null;
    cgoProjectID: string | null;
    blanketPurchaseOrder: string | null;
    ticketNumber: string | null;
    truckID: string | null;
    pdfViewUrl: string | null;
    pushedBy: string | null;
    pushedTimestamp: string | null;
    duplicatedTicketNumber?: boolean;
    batchId?: string;
    deletedReason?: string | null;
    deletedBy?: string | null;
}

export const pushTicketToAcumatica = async (
    instance: IPublicClientApplication,
    account: any,
    ticketId: string
): Promise<{ message: string }> => {
    const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: account
    });
    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${response.accessToken}`
        }
    };

    try {
        const apiUrl = `${API_URL}/tickets/${ticketId}/push`;
        const apiResponse = await axios.post<{ message: string }>(apiUrl, null, axiosConfig);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error pushing ticket ${ticketId} to Acumatica:`, error);
        if (axios.isAxiosError(error) && error.response?.data?.message) {
            throw new Error(`Failed to push: ${error.response.data.message}`);
        }
        throw error;
    }
};


export const pushBatchToAcumatica = async (
    instance: IPublicClientApplication,
    account: any,
    ticketIds: number[]
): Promise<any> => {
    const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: account
    });
    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json'
        }
    };
    const requestBody = {
        ticketIds: ticketIds
    };


    try {
        const apiUrl = `${API_URL}/tickets/push-batch`;
        const apiResponse = await axios.post(apiUrl, requestBody, axiosConfig);
        return apiResponse.data;
    } catch (error) {
        console.error("Error pushing batch of tickets:", error);
        if (axios.isAxiosError(error) && error.response?.data?.message) {
            throw new Error(`Failed to push batch: ${error.response.data.message}`);
        }
        throw error;
    }
}


export const reprocessDocument = async (
    instance: IPublicClientApplication,
    account: any,
    ticketId: string
): Promise<{ message: string }> => {
    const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: account
    });
    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${response.accessToken}`
        }
    };

    try {
        const apiUrl = `${API_URL}/documents/reprocess/${ticketId}`;
        const apiResponse = await axios.post<{ message: string }>(apiUrl, null, axiosConfig);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error reprocessing document ${ticketId}:`, error);
        if (axios.isAxiosError(error) && error.response?.data?.message) {
            throw new Error(`Failed to reprocess: ${error.response.data.message}`);
        }
        throw error;
    }
};

export const getCoverSheetSummaries = async (instance: IPublicClientApplication, account: any): Promise<CoverSheetSummaryDto[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` }
    };
    try {
        const apiResponse = await axios.get<CoverSheetSummaryDto[]>(`${API_URL}/coversheet/summaries`, config);
        return apiResponse.data;
    } catch (error) {
        console.error("Error fetching cover sheet summary:", error);
        throw error;
    }
};

export const getCoverSheetDetails = async (instance: IPublicClientApplication, account: any, batchID: string, scenario: number): Promise<CoverSheetDetailDto[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { BatchID: batchID, Scenario: scenario }
    };
    try {
        const apiResponse = await axios.get<CoverSheetDetailDto[]>(`${API_URL}/coversheet`, config);
        return apiResponse.data;
    } catch (error) {
        console.error("Error fetching cover sheet:", error);
        throw error;
    }
};

export const deleteCoverSheetDetails = async (instance: IPublicClientApplication, account: any, ids: number[]): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await Promise.all(ids.map(id => axios.delete(`${API_URL}/coversheet/${id}`, config)));
    } catch (error) {
        console.error(`Error deleting cover sheet detail:`, error);
        throw error;
    }
}

export const updateCoverSheetDetails = async (instance: IPublicClientApplication, account: any, updatedData: CoverSheetDetailDto[]): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await Promise.all(updatedData.map(data => axios.put(`${API_URL}/coversheet/${data.id}`, data, config)));
    } catch (error) {
        console.error(`Error updating cover sheet details:`, error);
        throw error;
    }
}

export const uploadCoverSheetDetails = async (instance: IPublicClientApplication, account: any, uploadData: CoverSheetDetailDto[]): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await Promise.all(uploadData.map(data => axios.post(`${API_URL}/coversheet`, data, config)));
    } catch (error) {
        console.error(`Error uploading cover sheet details:`, error);
        throw error;
    }
}

export const getCoverSheetReconciliation = async (instance: IPublicClientApplication, account: any, batchID: string): Promise<CoverSheetReconciliationDto[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { batchID: batchID }
    };
    try {
        const apiResponse = await axios.get<CoverSheetReconciliationDto[]>(`${API_URL}/coversheet/reconciliation`, config);
        return apiResponse.data;
    } catch (error) {
        console.error("Error fetching cover sheet reconciliation:", error);
        throw error;
    }
}

export interface CoverSheetDetailDto {
    id?: number;
    warehouse: string;
    inventoryID: string;
    branch: string;
    vendorID: string;
    freightVendorID: string;
    loads: number;
    receivedQty: number;
    uom: string;
    scenario: number;
    orderDate: Date;
    batchID: string;
}

export interface CoverSheetSummaryDto {
    batchID: string;
    orderDate: Date;
    warehouse: string;
    branch: string;
    issues: number;
}

export interface CoverSheetReconciliationDto {
    orderDate: string;
    branch: string;
    warehouse: string
    vendorId: string;
    freightVendorId: string;
    inventoryId: string;
    receivedQtyCover: number;
    receivedQtyScanned: number;
    receivedQtyVariance: number;
    ticketCountVariance: number;
    ticketCountCover: number;
    ticketCountScanned: number;
    status: string;
}

export const getNotes = async (instance: IPublicClientApplication, account: any, batchId: string): Promise<Note[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { batchId: batchId }
    };
    try {
        const apiResponse = await axios.get<Note[]>(`${API_URL}/notes`, config);
        return apiResponse.data;
    } catch (error) {
        console.error("Error fetching notes:", error);
        throw error;
    }
}

export const addNote = async (instance: IPublicClientApplication, account: any, note: Note): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await axios.post(`${API_URL}/notes`, note, config);
    } catch (error) {
        console.error(`Error adding note:`, error);
        throw error;
    }
}

export const updateNote = async (instance: IPublicClientApplication, account: any, note: Note): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await axios.put(`${API_URL}/notes/${note.id}`, note, config);
    } catch (error) {
        console.error(`Error updating note:`, error);
        throw error;
    }
}

export const deleteNote = async (instance: IPublicClientApplication, account: any, id: number): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        await axios.delete(`${API_URL}/notes/${id}`, config);
    } catch (error) {
        console.error(`Error deleting note:`, error);
        throw error;
    }
}

export interface Note {
    id: number;
    batchId: string;
    author: string;
    createdDateTime: Date;
    note: string;
}

export const getTicketMetadata = async (
    instance: IPublicClientApplication, 
    account: any
): Promise<TicketMetadata> => {
    const response = await instance.acquireTokenSilent({ 
        ...apiRequest, 
        account: account 
    });
    
    const config = { 
        headers: { Authorization: `Bearer ${response.accessToken}` } 
    };

    try {
        const apiResponse = await axios.get<TicketMetadata>(`${API_URL}/tickets/metadata`, config);
        return apiResponse.data;
    } catch (error) {
        console.error("Error fetching ticket metadata dropdowns:", error);
        throw error;
    }
};

export interface TicketMetadata {
    inventoryItems: string[];
    locations: string[];
    vendors: Array<{ vendorId: string; vendorIddesc: string }>;
    freightVendors: Array<{ freightVendorId: string; freightVendorIddesc: string }>;
}